import { useRef, useState } from "react";
import { Bold, Italic, Underline, Upload, X, AlignLeft, AlignCenter, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { autoEmbedImage } from "./imageEmbed";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BRAND_COLORS,
  FONT_OPTIONS,
  type BackgroundSettings,
  type TextStyle,
} from "./types";

interface TextStyleControlsProps {
  label: string;
  value: TextStyle | undefined;
  onChange: (next: TextStyle | undefined) => void;
  defaultColor: string;
  sizeMin?: number;
  sizeMax?: number;
  sizeDefault: number;
  /** Whether to show italic/underline toggles. Disable for eyebrows where they look bad. */
  showItalic?: boolean;
  showUnderline?: boolean;
  showBoldToggle?: boolean;
  showAlign?: boolean;
}

export function TextStyleControls({
  label,
  value,
  onChange,
  defaultColor,
  sizeMin = 12,
  sizeMax = 160,
  sizeDefault,
  showItalic = true,
  showUnderline = true,
  showBoldToggle = true,
  showAlign = true,
}: TextStyleControlsProps) {
  const v = value ?? {};
  const update = (patch: Partial<TextStyle>) => {
    const next = { ...v, ...patch };
    // Strip empty/undefined keys so the override stays minimal
    Object.keys(next).forEach((k) => {
      if (next[k as keyof TextStyle] === undefined) {
        delete next[k as keyof TextStyle];
      }
    });
    onChange(Object.keys(next).length === 0 ? undefined : next);
  };

  const currentSize =
    typeof v.fontSizePx === "number" ? v.fontSizePx : sizeDefault;
  const currentColor = v.color || defaultColor;
  const currentFont = v.fontFamily || FONT_OPTIONS[0].value;
  const isBold = v.bold ?? true;
  const isItalic = v.italic ?? false;
  const isUnderline = v.underline ?? false;
  const align = v.align ?? "left";

  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50/50">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
          {label}
        </Label>
        {value !== undefined && Object.keys(value).length > 0 && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-xs text-gray-500 hover:text-red-600 flex items-center gap-1"
            title="Reset to defaults"
          >
            <X className="h-3 w-3" />
            Reset
          </button>
        )}
      </div>

      {/* Font family */}
      <div>
        <Label className="text-xs text-gray-500">Font</Label>
        <Select
          value={currentFont}
          onValueChange={(val) => update({ fontFamily: val })}
        >
          <SelectTrigger className="h-8 text-xs mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map((f) => (
              <SelectItem
                key={f.value}
                value={f.value}
                style={{ fontFamily: f.value }}
              >
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Size slider */}
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs text-gray-500">Size</Label>
          <span className="text-xs text-gray-700 font-mono">
            {currentSize}px
          </span>
        </div>
        <input
          type="range"
          min={sizeMin}
          max={sizeMax}
          step={1}
          value={currentSize}
          onChange={(e) => update({ fontSizePx: Number(e.target.value) })}
          className="w-full mt-1 accent-emerald-600"
        />
      </div>

      {/* Color */}
      <div>
        <Label className="text-xs text-gray-500">Color</Label>
        <div className="flex gap-2 mt-1 flex-wrap">
          {BRAND_COLORS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => update({ color: c.value })}
              title={c.label}
              className={`w-6 h-6 rounded-full border-2 transition ${
                currentColor.toLowerCase() === c.value.toLowerCase()
                  ? "border-emerald-600 scale-110"
                  : "border-gray-200"
              }`}
              style={{ background: c.value }}
            />
          ))}
          <input
            type="color"
            value={currentColor}
            onChange={(e) => update({ color: e.target.value })}
            className="w-6 h-6 rounded-full border border-gray-200 cursor-pointer"
            title="Custom color"
          />
        </div>
      </div>

      {/* Toggles */}
      {(showBoldToggle || showItalic || showUnderline || showAlign) && (
        <div className="flex items-center gap-1 flex-wrap">
          {showBoldToggle && (
            <ToggleButton
              active={isBold}
              onClick={() => update({ bold: !isBold })}
              title={isBold ? "ExtraBold (brand default)" : "Regular weight"}
            >
              <Bold className="h-3.5 w-3.5" />
            </ToggleButton>
          )}
          {showItalic && (
            <ToggleButton
              active={isItalic}
              onClick={() => update({ italic: !isItalic })}
              title="Italic"
            >
              <Italic className="h-3.5 w-3.5" />
            </ToggleButton>
          )}
          {showUnderline && (
            <ToggleButton
              active={isUnderline}
              onClick={() => update({ underline: !isUnderline })}
              title="Underline"
            >
              <Underline className="h-3.5 w-3.5" />
            </ToggleButton>
          )}
          {showAlign && (
            <>
              <div className="w-px h-5 bg-gray-300 mx-1" />
              <ToggleButton
                active={align === "left"}
                onClick={() => update({ align: "left" })}
                title="Align left"
              >
                <AlignLeft className="h-3.5 w-3.5" />
              </ToggleButton>
              <ToggleButton
                active={align === "center"}
                onClick={() => update({ align: "center" })}
                title="Align center"
              >
                <AlignCenter className="h-3.5 w-3.5" />
              </ToggleButton>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-7 w-7 flex items-center justify-center rounded border transition ${
        active
          ? "bg-emerald-600 border-emerald-600 text-white"
          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
      }`}
    >
      {children}
    </button>
  );
}

// ─── Background controls ───────────────────────────────────────────────────

interface BackgroundControlsProps {
  value: BackgroundSettings | undefined;
  onChange: (next: BackgroundSettings | undefined) => void;
}

export function BackgroundControls({
  value,
  onChange,
}: BackgroundControlsProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const opacity = value?.opacity ?? 0.3;

  const handleFile = (file: File | null) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange({
        imageUrl: reader.result as string,
        opacity: value?.opacity ?? 0.3,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-gray-500">Background image</Label>
        <p className="text-xs text-gray-500 mt-0.5 mb-2">
          Overlays the FBK brand gradient. Keep opacity low so the green +
          gold glow stays visible.
        </p>

        {value?.imageUrl ? (
          <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-300">
            <img
              src={value.imageUrl}
              alt="Background"
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={() => onChange(undefined)}
              className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded p-1 shadow"
              title="Remove background"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-emerald-600 hover:text-emerald-700 transition"
          >
            <Upload className="h-5 w-5 mb-1" />
            <span className="text-xs font-semibold">Upload image</span>
            <span className="text-[10px] mt-0.5">PNG / JPG · under 5MB</span>
          </button>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0] || null)}
        />
      </div>

      {value?.imageUrl && (
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-gray-500">Opacity</Label>
            <span className="text-xs text-gray-700 font-mono">
              {Math.round(opacity * 100)}%
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(opacity * 100)}
            onChange={(e) =>
              onChange({
                ...value,
                opacity: Number(e.target.value) / 100,
              })
            }
            className="w-full mt-1 accent-emerald-600"
          />
        </div>
      )}
    </div>
  );
}

// ─── Content image upload ──────────────────────────────────────────────────

interface ImageUploadProps {
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  /** Optional second input — paste a remote URL */
  allowUrl?: boolean;
}

export function ImageUploadField({
  value,
  onChange,
  allowUrl = true,
}: ImageUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [embedding, setEmbedding] = useState(false);

  const handleFile = (file: File | null) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result as string);
    reader.readAsDataURL(file);
  };

  /** When the user blurs the URL input, try to fetch + embed the image
   *  as a data URL so html-to-image can capture it on export. */
  const handleUrlBlur = async (url: string) => {
    if (!url || url.startsWith("data:")) return;
    setEmbedding(true);
    const result = await autoEmbedImage(url);
    setEmbedding(false);
    if (result.success) {
      onChange(result.dataUrl);
      toast.success("Image embedded — export will work");
    } else {
      toast.warning(
        "Image URL is CORS-blocked — preview may work but export will fail. Use the Upload PNG button instead.",
      );
    }
  };

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-300 bg-gray-50">
          <img src={value} alt="Content" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="absolute top-1 right-1 bg-white/90 hover:bg-white text-red-600 rounded p-1 shadow"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-emerald-600 hover:text-emerald-700 transition"
        >
          <Upload className="h-5 w-5 mb-1" />
          <span className="text-xs font-semibold">Upload image</span>
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0] || null)}
      />
      {allowUrl && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-[10px] text-gray-400 uppercase tracking-widest">
            or
          </span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>
      )}
      {allowUrl && (
        <div className="relative">
          <Input
            value={value && !value.startsWith("data:") ? value : ""}
            onChange={(e) => onChange(e.target.value || undefined)}
            onBlur={(e) => handleUrlBlur(e.target.value)}
            disabled={embedding}
            placeholder="Paste image URL (auto-embeds on blur)"
            className="text-xs"
          />
          {embedding && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-gray-500 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Embedding…
            </span>
          )}
        </div>
      )}
    </div>
  );
}

